import React, { useState, useEffect, useMemo } from 'react';
import { Package, Tag, MapPin, CreditCard, FileText, Loader2, CheckCircle, Truck, Lock, Clock, AlertCircle, Info } from 'lucide-react';
import { useStoreDelivery, validateCoupon, placeOrder, useStorePaymentMethods, createTranzilaHandshake } from './hooks/useStoreApi';
import { getEffectiveUnitPrice, getSalePercent } from './utils/pricing';
import { useVocab, useIsAppointments } from './hooks/useVocab';

const TRANZILA_SCRIPT_URL = 'https://hf.tranzila.com/assets/js/thostedf.js';
const CREDIT_CARD_METHOD = 'אשראי';

// Module-scoped promise so we only inject the Tranzila script once across
// all CheckoutForm mounts.
let tranzilaScriptPromise = null;
function loadTranzilaScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.TzlaHostedFields) return Promise.resolve();
  if (tranzilaScriptPromise) return tranzilaScriptPromise;
  tranzilaScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = TRANZILA_SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { tranzilaScriptPromise = null; reject(new Error('Failed to load Tranzila script')); };
    document.head.appendChild(s);
  });
  return tranzilaScriptPromise;
}

// Labels for the fulfillment summary shown when the type was chosen in a previous step
const FULFILLMENT_TYPE_LABELS = {
  pickup: 'איסוף מנקודת חלוקה',
  delivery: 'משלוח עד הבית',
  takeaway: 'איסוף עצמי',
  eat_in: 'שירות במקום',
};
// On-premise types need no delivery point/area/address
const ON_PREMISE_TYPES = ['takeaway', 'eat_in'];

// Translate backend order errors (some English, some Hebrew) into a clear,
// friendly Hebrew message the customer can act on. Falls back to the raw
// message (or a generic line) when we don't recognise the error.
const friendlyOrderError = (rawMessage) => {
  const msg = (rawMessage || '').trim();
  if (!msg) return 'משהו השתבש בעת ביצוע ההזמנה. נסו שוב בעוד רגע.';

  // --- Product / category inventory ---
  const insufficient = msg.match(/Insufficient inventory for (.+)/i);
  if (insufficient) {
    return `המלאי של "${insufficient[1].trim()}" אזל או שלא נותרו מספיק יחידות. הפחיתו את הכמות בסל ונסו שוב.`;
  }
  if (/אזל המלאי/.test(msg)) return msg; // already a friendly Hebrew message from the server
  const notAvailable = msg.match(/^Product (.+) is not available$/i);
  if (notAvailable) {
    return `המוצר "${notAvailable[1].trim()}" אינו זמין יותר למכירה. הסירו אותו מהסל ונסו שוב.`;
  }
  if (/is not available$/i.test(msg)) {
    return 'אחד המוצרים בסל אינו זמין יותר. רעננו את הדף ונסו שוב.';
  }

  // --- Time slots ---
  if (/fully booked/i.test(msg)) {
    return 'מועד האיסוף שבחרתם התמלא בינתיים. חזרו אחורה ובחרו מועד אחר.';
  }
  const capacity = msg.match(/available items:\s*(\d+)/i);
  if (capacity) {
    const left = Number(capacity[1]);
    return left > 0
      ? `למועד שבחרתם נותר מקום ל-${left} פריטים בלבד. הפחיתו את הכמות בסל או בחרו מועד אחר.`
      : 'למועד שבחרתם לא נותר מקום. חזרו אחורה ובחרו מועד אחר.';
  }
  if (/Time slot selection is required/i.test(msg)) {
    return 'יש לבחור מועד איסוף כדי להשלים את ההזמנה.';
  }
  if (/time slot is not available|Invalid time slot|does not match the chosen date/i.test(msg)) {
    return 'מועד האיסוף שבחרתם כבר אינו זמין. חזרו אחורה ובחרו מועד אחר.';
  }

  // --- Delivery ---
  const minOrderMatch = msg.match(/Minimum order for this delivery area is (\d+)/i);
  if (minOrderMatch) {
    return `סכום ההזמנה נמוך מהמינימום למשלוח לאזור זה (₪${minOrderMatch[1]}). הוסיפו פריטים לסל ונסו שוב.`;
  }
  if (/Delivery point not found|Delivery area not found/i.test(msg)) {
    return 'נקודת/אזור המסירה שבחרתם אינם זמינים יותר. בחרו אפשרות אחרת ונסו שוב.';
  }

  // --- Payment ---
  if (/Payment amount does not match|Payment response failed verification|Payment was not successful|Payment was not approved/i.test(msg)) {
    return 'התשלום לא אושר. בדקו את פרטי הכרטיס ונסו שוב.';
  }

  // --- Generic fallbacks ---
  if (/Internal server error/i.test(msg)) {
    return 'אירעה תקלה זמנית בשרת. נסו שוב בעוד רגע.';
  }
  // If the server already sent Hebrew text, show it as-is; otherwise wrap generically.
  return /[֐-׿]/.test(msg) ? msg : `לא הצלחנו להשלים את ההזמנה: ${msg}`;
};

// Clean, rebuilt component after layout refactor. Ensures borders visible in viewport.
export const CheckoutForm = ({ storeSlug, cart, onSuccess, onCancel, modalLayout = false, preSelectedTimeSlot = null, preSelectedFulfillment = null }) => {
  const t = useVocab();
  const isAppt = useIsAppointments();
  // Unified fetch: { deliveryPoints: [], deliveryAreas: [] }
  const { deliveryPoints, deliveryAreas, loading: deliveryLoading } = useStoreDelivery(storeSlug);
  const { data: paymentMethodsData, loading: paymentMethodsLoading } = useStorePaymentMethods(storeSlug);

  // Check if any products in cart require delivery
  const requiresDelivery = cart.some(item => item.requires_delivery !== false);

  // Logged-in user id (used for coupon/referral attribution; null for guests)
  const clubUserId = (() => {
    try { const id = localStorage.getItem('chat_id'); return id && id !== 'guest' ? id : null; } catch { return null; }
  })();

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', fulfillmentType: preSelectedFulfillment?.type || 'pickup', deliveryPointId: '', deliveryAreaId: '', address: '', paymentMethod: '', notes: ''
  });
  const isOnPremise = ON_PREMISE_TYPES.includes(formData.fulfillmentType);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const scrollContainerRef = React.useRef(null);

  // Tranzila Hosted Fields state — populated when the customer picks "אשראי".
  // `tranzilaFields` is the object returned by TzlaHostedFields.create; we
  // call its .charge(...) method on submit.
  const [tranzilaFields, setTranzilaFields] = useState(null);
  const [tranzilaError, setTranzilaError] = useState(null);
  const [tranzilaLoading, setTranzilaLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  // Use pre-selected time slot from parent (from TimeSlotSelection component)
  // No need for local time slot state anymore

  // Auto-select fulfillment type if only one option is available.
  // Skipped when the type was already chosen in the fulfillment step
  // (including on-premise types that have no delivery point/area).
  useEffect(() => {
    if (preSelectedFulfillment || ON_PREMISE_TYPES.includes(formData.fulfillmentType)) return;
    if (!deliveryLoading) {
      const pickupEnabled = deliveryPoints && deliveryPoints.length > 0;
      const deliveryEnabled = deliveryAreas && deliveryAreas.length > 0;

      // If only pickup is available, auto-select it
      if (pickupEnabled && !deliveryEnabled && formData.fulfillmentType !== 'pickup') {
        setFormData(prev => ({ ...prev, fulfillmentType: 'pickup', deliveryAreaId: '' }));
      }
      // If only delivery is available, auto-select it
      else if (!pickupEnabled && deliveryEnabled && formData.fulfillmentType !== 'delivery') {
        setFormData(prev => ({ ...prev, fulfillmentType: 'delivery', deliveryPointId: '' }));
      }
    }
  }, [deliveryLoading, deliveryPoints, deliveryAreas, formData.fulfillmentType, preSelectedFulfillment]);

  // Auto-select single delivery point
  useEffect(() => {
    if (!deliveryLoading && deliveryPoints?.length === 1 && formData.fulfillmentType === 'pickup' && !formData.deliveryPointId) {
      setFormData(prev => ({ ...prev, deliveryPointId: deliveryPoints[0].delivery_point_id || deliveryPoints[0].dp_id }));
    }
  }, [deliveryPoints, deliveryLoading, formData.fulfillmentType, formData.deliveryPointId]);

  // Auto-select single delivery area
  useEffect(() => {
    if (!deliveryLoading && deliveryAreas?.length === 1 && formData.fulfillmentType === 'delivery' && !formData.deliveryAreaId) {
      setFormData(prev => ({ ...prev, deliveryAreaId: deliveryAreas[0].delivery_area_id }));
    }
  }, [deliveryAreas, deliveryLoading, formData.fulfillmentType, formData.deliveryAreaId]);

  // Auto-select single payment method
  useEffect(() => {
    if (!paymentMethodsLoading && paymentMethodsData?.payment_methods?.length === 1 && !formData.paymentMethod) {
      setFormData(prev => ({ ...prev, paymentMethod: paymentMethodsData.payment_methods[0] }));
    }
  }, [paymentMethodsData, paymentMethodsLoading, formData.paymentMethod]);

  // Lazy-load and mount Tranzila Hosted Fields when the customer picks credit
  // card. We rely on the container divs being rendered first (they live under
  // a `paymentMethod === CREDIT_CARD_METHOD` guard in JSX), so this effect
  // runs after the DOM has those nodes.
  useEffect(() => {
    if (formData.paymentMethod !== CREDIT_CARD_METHOD) return;
    if (tranzilaFields) return; // already mounted
    let cancelled = false;
    setTranzilaLoading(true);
    setTranzilaError(null);
    loadTranzilaScript()
      .then(() => {
        if (cancelled) return;
        if (!window.TzlaHostedFields) throw new Error('Tranzila script unavailable');
        const fields = window.TzlaHostedFields.create({
          sandbox: false,
          fields: {
            credit_card_number: { selector: '#tz_credit_card_number', placeholder: '0000 0000 0000 0000', tabindex: 1 },
            cvv: { selector: '#tz_cvv', placeholder: '123', tabindex: 3 },
            expiry: { selector: '#tz_expiry', placeholder: 'MM/YY', version: '1', tabindex: 2 },
          },
          styles: {
            input: { 'font-size': '14px', 'font-family': 'inherit', color: '#1a1a1a', height: '36px' },
            select: { 'font-size': '14px', 'font-family': 'inherit', color: '#1a1a1a', height: '36px' },
          },
        });
        setTranzilaFields(fields);
      })
      .catch((err) => {
        if (!cancelled) setTranzilaError(err.message || 'לא ניתן לטעון את שירות התשלום');
      })
      .finally(() => {
        if (!cancelled) setTranzilaLoading(false);
      });
    return () => { cancelled = true; };
  }, [formData.paymentMethod, tranzilaFields]);

  // Subtotal now includes any selected paid options (each option price multiplied by quantity)
  // Sale discount is applied to the base price only; paid options remain full price.
  const subtotal = cart.reduce((sum, item) => {
    const basePrice = getEffectiveUnitPrice(item);
    const qty = item.quantity || 0;
    const optionsExtra = (item.selectedPaidOptions || []).reduce((optSum, opt) => {
      const optPrice = typeof opt.price === 'number' ? opt.price : parseFloat(opt.price);
      return optSum + (Number.isFinite(optPrice) ? optPrice : 0);
    }, 0);
    const line = basePrice + optionsExtra;
    return sum + line * qty;
  }, 0);

  let discount = 0;
  if (appliedCoupon?.valid) {
    if (appliedCoupon.discount != null) {
      const numeric = typeof appliedCoupon.discount === 'number' ? appliedCoupon.discount : parseFloat(appliedCoupon.discount);
      if (Number.isFinite(numeric)) discount = numeric;
    }
    if (discount === 0 && appliedCoupon.discount_type && appliedCoupon.discount_value != null) {
      const val = typeof appliedCoupon.discount_value === 'number' ? appliedCoupon.discount_value : parseFloat(appliedCoupon.discount_value);
      if (Number.isFinite(val)) {
        discount = appliedCoupon.discount_type === 'percentage' ? subtotal * (val / 100) : val;
      }
    }
  }
  if (discount > subtotal) discount = subtotal;
  // Delivery fee/min order (only for delivery fulfillment and if products require delivery)
  const selectedArea = requiresDelivery && formData.fulfillmentType === 'delivery'
    ? deliveryAreas.find(a => a.delivery_area_id === formData.deliveryAreaId)
    : null;
  const deliveryFee = requiresDelivery && selectedArea?.fee ? Number(selectedArea.fee) : 0;
  const minOrder = selectedArea?.min_order ? Number(selectedArea.min_order) : 0;
  const total = Math.max(0, subtotal - discount + deliveryFee);

  const handlePaymentMethodClick = React.useCallback((method) => {
    const scrollPos = scrollContainerRef.current?.scrollTop;

    setFormData(prev => ({ ...prev, paymentMethod: method }));
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.paymentMethod;
      return newErrors;
    });

    // Force scroll position to stay the same
    if (scrollContainerRef.current && scrollPos !== undefined) {
      scrollContainerRef.current.scrollTop = scrollPos;
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPos;
        }
      });
    }
  }, []);

  const handleChange = React.useCallback((e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      if (name === 'fulfillmentType') {
        // Reset opposite selection when switching modes
        return { ...prev, fulfillmentType: value, deliveryPointId: '', deliveryAreaId: '' };
      }
      return { ...prev, [name]: value };
    });
    setErrors(prev => {
      if (prev[name]) {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });
  }, []);

  const handleBlur = React.useCallback((e) => {
    const { name, value } = e.target;
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };

      if (name === 'name') {
        if (!value.trim()) newErrors.name = 'שם מלא הוא שדה חובה';
        else delete newErrors.name;
      } else if (name === 'phone') {
        if (!value.trim()) newErrors.phone = 'מספר טלפון הוא שדה חובה';
        else if (!validatePhone(value)) newErrors.phone = 'מספר טלפון לא תקין';
        else delete newErrors.phone;
      } else if (name === 'address') {
        if (requiresDelivery && formData.fulfillmentType === 'delivery' && !value.trim()) {
          newErrors.address = 'יש להזין כתובת מלאה למשלוח';
        } else {
          delete newErrors.address;
        }
      }

      return newErrors;
    });
  }, [requiresDelivery, formData.fulfillmentType]);

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\s/g, '');
    // Israeli phone format:
    // Mobile: 10 digits (05XXXXXXXX) or with hyphen (05X-XXXXXXX)
    // Landline: 9 digits (0XXXXXXXX) or with hyphen (0X-XXXXXXX)
    // Check for valid formats
    if (/^05\d-\d{7}$/.test(cleaned)) return true; // Mobile with hyphen: 05X-XXXXXXX
    if (/^05\d{8}$/.test(cleaned)) return true; // Mobile without hyphen: 05XXXXXXXX (10 digits)
    if (/^0[2-4,8-9]-\d{7}$/.test(cleaned)) return true; // Landline with hyphen: 0X-XXXXXXX
    if (/^0[2-4,8-9]\d{7}$/.test(cleaned)) return true; // Landline without hyphen: 0XXXXXXXX (9 digits)
    return false;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'שם מלא הוא שדה חובה';
    if (!formData.phone.trim()) newErrors.phone = 'מספר טלפון הוא שדה חובה';
    else if (!validatePhone(formData.phone)) newErrors.phone = 'מספר טלפון לא תקין';

    // Only validate delivery/fulfillment if products require delivery.
    // On-premise types (takeaway / eat-in) need no extra details.
    if (requiresDelivery && !isOnPremise) {
      if (formData.fulfillmentType === 'pickup') {
        if (!formData.deliveryPointId) newErrors.deliveryPointId = 'יש לבחור נקודת איסוף';
      } else {
        if (!formData.deliveryAreaId) newErrors.deliveryAreaId = 'יש לבחור אזור משלוח';
        if (minOrder && subtotal < minOrder) newErrors.deliveryAreaId = `מינימום הזמנה לאזור זה הוא ₪${minOrder}`;
        if (!formData.address.trim()) newErrors.address = 'יש להזין כתובת מלאה למשלוח';
      }
    }

    if (!formData.paymentMethod) newErrors.paymentMethod = 'יש לבחור אמצעי תשלום';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) { setCouponError('יש להזין קוד קופון'); return; }
    setValidatingCoupon(true); setCouponError('');
    try {
      const result = await validateCoupon(storeSlug, couponCode, subtotal, clubUserId);
      if (result && result.valid === false) { setCouponError(result.error || 'קוד לא תקין'); setAppliedCoupon(null); return; }
      setAppliedCoupon(result); setCouponError('');
    } catch (err) {
      setCouponError(err.message || 'קוד קופון לא תקין'); setAppliedCoupon(null);
    } finally { setValidatingCoupon(false); }
  };

  // Time slot data comes from preSelectedTimeSlot prop (selected in TimeSlotSelection component)

  // Wrap Tranzila's callback-style charge in a Promise.
  const chargeCardWithTranzila = (amount, terminalName, thtk) => new Promise((resolve, reject) => {
    if (!tranzilaFields) return reject(new Error('שדות אשראי לא מוכנים'));
    tranzilaFields.charge({
      terminal_name: terminalName,
      amount: amount.toFixed(2),
      thtk,
      contact: formData.name,
      email: formData.email || '',
      response_language: 'hebrew',
    }, (err, response) => {
      if (err) {
        const msg = Array.isArray(err.messages) && err.messages.length
          ? err.messages.map((m) => m.message).filter(Boolean).join(', ')
          : (err.message || 'התשלום נדחה');
        return reject(new Error(msg));
      }
      const txn = response?.transaction_response;
      if (!txn?.success) {
        return reject(new Error(txn?.error || 'התשלום נכשל'));
      }
      resolve(response);
    });
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validateForm()) return;
    const isCreditCard = formData.paymentMethod === CREDIT_CARD_METHOD;
    if (isCreditCard && !tranzilaFields) {
      setTranzilaError('שדות האשראי עדיין נטענים, נסה שוב מיד');
      return;
    }
    setSubmitting(true);
    if (isCreditCard) setPaymentProcessing(true);
    try {
      const orderData = {
        customer: { name: formData.name, email: formData.email || null, phone: formData.phone },
        // Include address if delivery selected and required
        customer_address: requiresDelivery && formData.fulfillmentType === 'delivery' ? formData.address.trim() || null : null,
        // On-premise types apply even when the cart has no delivery-requiring products
        fulfillment_type: isOnPremise
          ? formData.fulfillmentType
          : (!requiresDelivery ? 'none' : ({ pickup: 'delivery_point', delivery: 'delivery_area' }[formData.fulfillmentType] || 'delivery_point')),
        delivery_point_id: requiresDelivery && formData.fulfillmentType === 'pickup' ? formData.deliveryPointId : null,
        delivery_area_id: requiresDelivery && formData.fulfillmentType === 'delivery' ? formData.deliveryAreaId : null,
        delivery_fee: requiresDelivery && !isOnPremise ? (deliveryFee || 0) : 0,
        payment_method: formData.paymentMethod,
        notes: formData.notes || null,
        items: cart.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          // Only send paid_options with required name; backend will derive IDs from objects
          paid_options: (i.selectedPaidOptions || []).map(o => ({ id: o.id, name: o.name, price: o.price }))
        })),
        coupon_code: appliedCoupon ? couponCode : null,
        user_id: clubUserId,
        // Only include time slot data if enabled and both slotId and date are present
        ...(preSelectedTimeSlot?.enabled && preSelectedTimeSlot?.slotId && preSelectedTimeSlot?.date ? {
          time_slot_id: preSelectedTimeSlot.slotId,
          time_slot_date: preSelectedTimeSlot.date,
        } : {}),
      };

      let tranzilaResponse = null;
      if (isCreditCard) {
        // 1) handshake (server-side, binds the thtk to this exact amount)
        const { thtk, terminal_name } = await createTranzilaHandshake(storeSlug, total);
        // 2) charge via Hosted Fields iframe (card data never reaches our server)
        tranzilaResponse = await chargeCardWithTranzila(total, terminal_name, thtk);
        orderData.tranzila_response = tranzilaResponse;
      }

      const result = await placeOrder(storeSlug, orderData);
      const enriched = {
        ...result,
        _original_api: result,
        customer_name: result.customer?.name || orderData.customer.name,
        customer_phone: result.customer?.phone || orderData.customer.phone,
        customer_email: result.customer?.email || orderData.customer.email,
        customer_address: orderData.customer_address,
        payment_method: result.payment_method || orderData.payment_method || formData.paymentMethod,
        fulfillment_type: orderData.fulfillment_type,
        // Carry the customer-facing label so the confirmation screen shows it
        ...(isOnPremise && preSelectedFulfillment?.label ? { delivery_location_name: preSelectedFulfillment.label } : {}),
        delivery_point_id: orderData.delivery_point_id,
        delivery_area_id: orderData.delivery_area_id,
        delivery_fee: deliveryFee,
        items: cart.map(item => ({
          ...item,
          product_name: item.name,
          paid_options: (item.selectedPaidOptions || []).map(o => ({ name: o.name, price: o.price }))
        })),
        subtotal, discount, deliveryFee, total,
        tranzila_transaction_id: tranzilaResponse?.transaction_response?.transaction_id,
        // Carry the selected time slot so the confirmation screen can show it
        ...(preSelectedTimeSlot?.enabled && preSelectedTimeSlot?.slotId ? { time_slot: preSelectedTimeSlot } : {}),
      };
      onSuccess(enriched);
    } catch (err) {
      const friendly = friendlyOrderError(err.message);
      setSubmitError(friendly);
      // Bring the error into view (it renders just above the submit button).
      requestAnimationFrame(() => {
        scrollContainerRef.current?.scrollTo?.({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' });
      });
    } finally {
      setSubmitting(false);
      setPaymentProcessing(false);
    }
  };

  const isFormValidBase = formData.name.trim() && formData.phone.trim() && validatePhone(formData.phone) && formData.paymentMethod;
  const fulfillmentValid = !requiresDelivery || isOnPremise || (formData.fulfillmentType === 'pickup'
    ? !!formData.deliveryPointId
    : !!formData.deliveryAreaId && (!minOrder || subtotal >= minOrder) && formData.address.trim());
  const ccReady = formData.paymentMethod !== CREDIT_CARD_METHOD || !!tranzilaFields;
  const isFormValid = isFormValidBase && fulfillmentValid && ccReady;

  // Human-readable list of what's still blocking the submit button, so the
  // customer understands why it's disabled (feedback: it wasn't always clear).
  const missingRequirements = useMemo(() => {
    const items = [];
    if (!formData.name.trim()) items.push('שם מלא');
    if (!formData.phone.trim()) items.push('מספר טלפון');
    else if (!validatePhone(formData.phone)) items.push('מספר טלפון תקין');
    if (requiresDelivery && !isOnPremise) {
      if (formData.fulfillmentType === 'pickup') {
        if (!formData.deliveryPointId) items.push('בחירת נקודת איסוף');
      } else {
        if (!formData.deliveryAreaId) items.push('בחירת אזור משלוח');
        else if (minOrder && subtotal < minOrder) items.push(`השלמת מינימום הזמנה (₪${minOrder})`);
        if (!formData.address.trim()) items.push('כתובת מלאה למשלוח');
      }
    }
    if (!formData.paymentMethod) items.push('בחירת אמצעי תשלום');
    else if (!ccReady) items.push('סיום טעינת טופס האשראי');
    return items;
  }, [formData.name, formData.phone, formData.fulfillmentType, formData.deliveryPointId, formData.deliveryAreaId, formData.address, formData.paymentMethod, requiresDelivery, isOnPremise, minOrder, subtotal, ccReady]);
  const [footerHeight, setFooterHeight] = useState(88); // fallback
  useEffect(() => {
    const measure = () => {
      const el = document.getElementById('footer-nav');
      if (el) {
        const h = el.getBoundingClientRect().height;
        if (h && h !== footerHeight) setFooterHeight(h);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [footerHeight]);
  const FOOTER_RESERVED = footerHeight + 16; // include extra gap

  // Memoize sub-components to prevent recreating them on every render (which causes input focus loss)
  const Summary = useMemo(() => (
    <div className="space-y-3">
      {cart.map(item => {
        const basePrice = getEffectiveUnitPrice(item);
        const salePct = getSalePercent(item);
        const qty = item.quantity || 0;
        const optionsExtra = (item.selectedPaidOptions || []).reduce((optSum, opt) => {
          const optPrice = typeof opt.price === 'number' ? opt.price : parseFloat(opt.price);
          return optSum + (Number.isFinite(optPrice) ? optPrice : 0);
        }, 0);
        const lineUnit = basePrice + optionsExtra;
        const lineTotal = lineUnit * qty;
        return (
          <div key={item.product_id} className="text-xs sm:text-sm space-y-0.5">
            <div className="flex justify-between items-center">
              <div className="flex-1 truncate">
                <span className="font-semibold text-mekomit-900">{item.name}</span>
                <span className="text-mekomit-700 mr-2">× {qty}</span>
                {salePct > 0 && (
                  <span className="text-[10px] sm:text-xs bg-danger-100 text-danger-700 px-1.5 py-0.5 rounded-full font-bold mr-1.5">-{salePct}%</span>
                )}
              </div>
              <span className="font-bold text-mekomit-900">₪{lineTotal.toFixed(2)}</span>
            </div>
            {item.selectedPaidOptions && item.selectedPaidOptions.length > 0 && (
              <div className="flex justify-between pl-2">
                <div className="flex-1 truncate text-mekomit-600">
                  {item.selectedPaidOptions.map(o => o.name).join(', ')}
                </div>
                <span className="text-mekomit-600">+₪{(optionsExtra * qty).toFixed(2)}</span>
              </div>
            )}
          </div>
        );
      })}
      <div className="border-t border-mekomit-200 pt-2 mt-2 space-y-1 text-xs sm:text-sm">
        <div className="flex justify-between"><span className="text-mekomit-700">סכום ביניים:</span><span className="font-semibold text-mekomit-900">₪{subtotal.toFixed(2)}</span></div>
        {appliedCoupon && <div className="flex justify-between text-success-600"><span>הנחה ({couponCode}):</span><span className="font-semibold">-₪{Number.isFinite(discount) ? discount.toFixed(2) : '0.00'}</span></div>}
        {requiresDelivery && formData.fulfillmentType === 'delivery' && deliveryFee > 0 && (
          <div className="flex justify-between text-mekomit-700"><span>דמי משלוח:</span><span className="font-semibold text-mekomit-900">₪{deliveryFee.toFixed(2)}</span></div>
        )}
        <div className="flex justify-between text-base font-bold text-mekomit-900 border-t border-mekomit-200 pt-1"><span>סה"כ:</span><span>₪{total.toFixed(2)}</span></div>
      </div>
    </div>
  ), [cart, subtotal, appliedCoupon, couponCode, discount, requiresDelivery, formData.fulfillmentType, deliveryFee]);

  const FulfillmentSelector = useMemo(() => {
    // Type already chosen in the fulfillment step — show a summary instead of a selector
    if (preSelectedFulfillment) {
      if (preSelectedFulfillment.type === 'none') return null;
      const label = (preSelectedFulfillment.label || '').trim() || FULFILLMENT_TYPE_LABELS[formData.fulfillmentType] || formData.fulfillmentType;
      return (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-mekomit-50 border border-mekomit-200 text-xs">
          <Truck className="w-4 h-4 text-mekomit-600 shrink-0" />
          <span className="text-mekomit-700">אופן קבלת ההזמנה:</span>
          <span className="font-bold text-mekomit-900">{label}</span>
        </div>
      );
    }

    // Check which options are actually available
    const pickupEnabled = !deliveryLoading && deliveryPoints && deliveryPoints.length > 0;
    const deliveryEnabled = !deliveryLoading && deliveryAreas && deliveryAreas.length > 0;

    const availableOptions = [
      { value: 'pickup', label: 'איסוף עצמי', enabled: pickupEnabled },
      { value: 'delivery', label: 'משלוח לבית', enabled: deliveryEnabled }
    ].filter(opt => opt.enabled);

    // If only one option is available, don't show the selector
    if (availableOptions.length <= 1) {
      return null;
    }

    return (
      <div className="space-y-2">
        <span className="block text-xs font-semibold text-mekomit-900 flex items-center gap-1"><Truck className="w-4 h-4" /> סוג אספקה <span className="text-danger-500">*</span></span>
        <div className="grid grid-cols-2 gap-2">
          {availableOptions.map(opt => (
            <label key={opt.value} className={`flex items-center justify-center gap-1 px-2 py-2 rounded-lg border-2 cursor-pointer transition-all text-xs ${formData.fulfillmentType === opt.value ? 'border-mekomit-500 bg-mekomit-50 font-bold shadow-md' : 'border-mekomit-200 hover:border-mekomit-300'}`}>
              <input type="radio" name="fulfillmentType" value={opt.value} checked={formData.fulfillmentType === opt.value} onChange={handleChange} className="sr-only" />
              <span className="text-mekomit-900">{opt.label}</span>
            </label>
          ))}
        </div>
        {formData.fulfillmentType === 'delivery' && selectedArea && minOrder > 0 && subtotal < minOrder && (
          <p className="text-xs text-danger-500">מינימום הזמנה לאזור זה: ₪{minOrder} (כרגע ₪{subtotal.toFixed(2)})</p>
        )}
      </div>
    );
  }, [formData.fulfillmentType, selectedArea, minOrder, subtotal, deliveryLoading, deliveryPoints, deliveryAreas, preSelectedFulfillment]);

  const PickupOrDeliveryFields = useMemo(() => {
    // On-premise service: nothing more to fill in
    if (ON_PREMISE_TYPES.includes(formData.fulfillmentType)) {
      return null;
    }
    if (formData.fulfillmentType === 'pickup') {
      return (
        <div className="space-y-1">
          <label htmlFor="deliveryPoint" className="block text-xs font-semibold text-mekomit-900 flex items-center gap-1"><MapPin className="w-4 h-4" /> נקודת איסוף <span className="text-danger-500">*</span></label>
          {deliveryLoading ? <div className="text-mekomit-700 text-xs">טוען נקודות...</div> : deliveryPoints?.length ? (
            <>
              <select id="deliveryPoint" name="deliveryPointId" value={formData.deliveryPointId} onChange={handleChange} className={`w-full px-3 py-2 rounded-lg border ${errors.deliveryPointId ? 'border-danger-500 ring-2 ring-danger-500/20' : 'border-mekomit-200'} focus:outline-none focus:ring-2 focus:ring-mekomit-500 transition-all text-xs`} style={{ maxHeight: '40px', overflow: 'hidden' }}>
                <option value="">בחר נקודת איסוף</option>
                {deliveryPoints.map(dp => <option key={dp.delivery_point_id || dp.dp_id} value={dp.delivery_point_id || dp.dp_id}>{dp.name} - {dp.address}</option>)}
              </select>
              {errors.deliveryPointId && <p className="text-danger-500 text-xs">{errors.deliveryPointId}</p>}
            </>
          ) : <div className="text-danger-500 text-xs">אין נקודות זמינות</div>}
        </div>
      );
    }
    // Delivery flow: keep address input mounted (disabled until area selected) to prevent focus loss due to conditional remounts.
    return (
      <div className="space-y-2">
        <div className="space-y-1">
          <label htmlFor="deliveryArea" className="block text-xs font-semibold text-mekomit-900 flex items-center gap-1"><MapPin className="w-4 h-4" /> אזור משלוח <span className="text-danger-500">*</span></label>
          {deliveryLoading ? <div className="text-mekomit-700 text-xs">טוען אזורים...</div> : deliveryAreas?.length ? (
            <>
              <select id="deliveryArea" name="deliveryAreaId" value={formData.deliveryAreaId} onChange={handleChange} className={`w-full px-3 py-2 rounded-lg border ${errors.deliveryAreaId ? 'border-danger-500 ring-2 ring-danger-500/20' : 'border-mekomit-200'} focus:outline-none focus:ring-2 focus:ring-mekomit-500 transition-all text-xs`} style={{ maxHeight: '40px', overflow: 'hidden' }}>
                <option value="">בחר אזור משלוח</option>
                {deliveryAreas.map(a => (
                  <option key={a.delivery_area_id} value={a.delivery_area_id}>{a.name} (מינ' ₪{a.min_order} | משלוח ₪{a.fee})</option>
                ))}
              </select>
              {errors.deliveryAreaId && <p className="text-danger-500 text-xs">{errors.deliveryAreaId}</p>}
              {formData.deliveryAreaId && selectedArea && (
                <p className="text-xs text-mekomit-700">משלוח: ₪{selectedArea.fee} | מינימום הזמנה: ₪{selectedArea.min_order}</p>
              )}
            </>
          ) : <div className="text-danger-500 text-xs">אין אזורי משלוח זמינים</div>}
        </div>
        <div className="space-y-1 mt-1">
          <label htmlFor="address" className="block text-xs font-semibold text-mekomit-900">כתובת מלאה למשלוח <span className="text-danger-500">*</span></label>
          <input
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={!formData.deliveryAreaId}
            autoComplete="street-address"
            className={`w-full px-3 py-2 rounded-lg border ${errors.address ? 'border-danger-500 ring-2 ring-danger-500/20' : 'border-mekomit-200'} focus:outline-none focus:ring-2 focus:ring-mekomit-500 transition-all text-xs disabled:bg-surface-muted disabled:cursor-not-allowed`}
            placeholder={formData.deliveryAreaId ? 'רחוב, מספר בית, קומה, דירה' : 'בחר אזור משלוח קודם'}
          />
          {errors.address && <p className="text-danger-500 text-xs">{errors.address}</p>}
        </div>
      </div>
    );
  }, [formData.fulfillmentType, formData.deliveryPointId, formData.deliveryAreaId, formData.address, deliveryLoading, deliveryPoints, deliveryAreas, errors, selectedArea]);

  const CreditCardFields = useMemo(() => {
    if (formData.paymentMethod !== CREDIT_CARD_METHOD) return null;
    return (
      <div className="space-y-3 p-4 bg-white border-2 border-mekomit-300 rounded-xl">
        <p className="text-[11px] text-mekomit-700 flex items-center gap-1">
          <Lock className="w-3.5 h-3.5" />
          פרטי הכרטיס מועברים מוצפנים ישירות ל-Tranzila ולא נשמרים אצלנו.
        </p>
        {tranzilaLoading && (
          <div className="flex items-center gap-2 text-xs text-mekomit-700"><Loader2 className="w-4 h-4 animate-spin" /> טוען טופס תשלום מאובטח...</div>
        )}
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-mekomit-900" htmlFor="tz_credit_card_number">מספר כרטיס</label>
          <div id="tz_credit_card_number" className="px-3 py-1.5 border border-mekomit-200 rounded-lg bg-white" style={{ minHeight: '40px' }} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-mekomit-900" htmlFor="tz_expiry">תוקף</label>
            <div id="tz_expiry" className="px-3 py-1.5 border border-mekomit-200 rounded-lg bg-white" style={{ minHeight: '40px' }} />
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-mekomit-900" htmlFor="tz_cvv">CVV</label>
            <div id="tz_cvv" className="px-3 py-1.5 border border-mekomit-200 rounded-lg bg-white" style={{ minHeight: '40px' }} />
          </div>
        </div>
        {tranzilaError && <p className="text-danger-500 text-xs">{tranzilaError}</p>}
      </div>
    );
  }, [formData.paymentMethod, tranzilaLoading, tranzilaError]);

  // Show the time slot the customer picked earlier (TimeSlotSelection step), if any.
  const TimeSlotInfo = useMemo(() => {
    if (!preSelectedTimeSlot?.enabled || !preSelectedTimeSlot?.slotId) return null;
    const datePart = preSelectedTimeSlot.dateLabel || preSelectedTimeSlot.date || '';
    const timePart = preSelectedTimeSlot.startTime && preSelectedTimeSlot.endTime
      ? `${preSelectedTimeSlot.startTime} - ${preSelectedTimeSlot.endTime}`
      : (preSelectedTimeSlot.slotLabel || '');
    if (!datePart && !timePart) return null;
    return (
      <div className="flex items-center gap-2 mt-3 pt-2 border-t border-mekomit-200 text-xs sm:text-sm">
        <Clock className="w-4 h-4 text-mekomit-700 shrink-0" />
        <span className="font-semibold text-mekomit-900">מועד איסוף:</span>
        <span className="text-mekomit-700">{[datePart, timePart].filter(Boolean).join(' · ')}</span>
      </div>
    );
  }, [preSelectedTimeSlot]);

  const CouponField = useMemo(() => (
    <div className="space-y-1">
      <label htmlFor="coupon" className="block text-xs font-semibold text-mekomit-900 flex items-center gap-1"><Tag className="w-4 h-4" /> קוד קופון</label>
      <div className="flex gap-1">
        <input id="coupon" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} disabled={appliedCoupon !== null} className="flex-1 px-3 py-2 rounded-lg border border-mekomit-200 focus:outline-none focus:ring-2 focus:ring-mekomit-500 transition-all disabled:bg-surface-muted" placeholder="הזן קוד" />
        {!appliedCoupon ? (
          <button type="button" onClick={handleApplyCoupon} disabled={validatingCoupon || !couponCode.trim()} className="px-4 py-2 bg-mekomit-500 hover:bg-mekomit-600 disabled:bg-line text-white font-semibold rounded-lg transition-all flex items-center gap-2 text-xs">{validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'החל'}</button>
        ) : (
          <button type="button" onClick={() => { setAppliedCoupon(null); setCouponCode(''); }} className="px-4 py-2 bg-danger-500 hover:bg-danger-600 text-white font-semibold rounded-lg transition-all text-xs">הסר</button>
        )}
      </div>
      {couponError && <p className="text-danger-500 text-xs">{couponError}</p>}
      {appliedCoupon && <p className="text-success-600 text-xs flex items-center gap-1"><CheckCircle className="w-4 h-4" /> חסכת ₪{Number.isFinite(discount) ? discount.toFixed(2) : '0.00'}</p>}
    </div>
  ), [couponCode, appliedCoupon, validatingCoupon, couponError, discount, handleApplyCoupon]);

  // Feedback shown just above the submit button: a friendly error when an
  // order attempt failed, plus a hint listing what's still missing/invalid
  // when the button is disabled (so it's clear why it can't be pressed yet).
  const SubmitFeedback = (
    <>
      {submitError && (
        <div role="alert" className="flex items-start gap-2 text-xs sm:text-sm bg-danger-50 border border-danger-100 text-danger-700 rounded-lg p-2.5">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{submitError}</span>
        </div>
      )}
      {!submitError && !isFormValid && !submitting && missingRequirements.length > 0 && (
        <div className="flex items-start gap-2 text-xs bg-info-50 border border-info-100 text-mekomit-700 rounded-lg p-2.5">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-info-500" />
          <span>
            כדי להשלים את ההזמנה יש למלא: {missingRequirements.join(', ')}
          </span>
        </div>
      )}
    </>
  );

  if (modalLayout) {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col h-full w-full min-h-0" style={{ maxHeight: '100%', overflow: 'hidden' }}>
        {/* Scrollable content area */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4 space-y-4" style={{ scrollBehavior: 'auto', WebkitOverflowScrolling: 'touch', minHeight: 0, position: 'relative', willChange: 'scroll-position' }}>
          <section className="bg-mekomit-50 rounded-xl p-3 border border-mekomit-100 shadow-sm">
            <h3 className="text-xl font-bold text-mekomit-900 mb-3 flex items-center gap-2"><Package className="w-5 h-5" /> {isAppt ? 'סיכום התור' : 'סיכום הזמנה'}</h3>
            {Summary}
            {TimeSlotInfo}
          </section>
          <div className="space-y-3">
            <h3 className="text-xl font-bold text-mekomit-900">{isAppt ? 'פרטי המטופל' : 'פרטי הזמנה'}</h3>
            <div className="space-y-1">
              <label htmlFor="name" className="block text-xs font-semibold text-mekomit-900">שם מלא <span className="text-danger-500">*</span></label>
              <input id="name" name="name" value={formData.name} onChange={handleChange} onBlur={handleBlur} autoComplete="name" className={`w-full px-3 py-2 rounded-lg border ${errors.name ? 'border-danger-500 ring-2 ring-danger-500/20' : 'border-mekomit-200'} focus:outline-none focus:ring-2 focus:ring-mekomit-500 transition-all`} placeholder="הזן שם מלא" />
              {errors.name && <p className="text-danger-500 text-xs">{errors.name}</p>}
            </div>
            <div className="space-y-1">
              <label htmlFor="email" className="block text-xs font-semibold text-mekomit-900">אימייל (אופציונלי)</label>
              <input id="email" name="email" type="email" inputMode="email" value={formData.email} onChange={handleChange} autoComplete="email" className="w-full px-3 py-2 rounded-lg border border-mekomit-200 focus:outline-none focus:ring-2 focus:ring-mekomit-500 transition-all" placeholder="example@email.com" />
            </div>
            <div className="space-y-1">
              <label htmlFor="phone" className="block text-xs font-semibold text-mekomit-900">טלפון <span className="text-danger-500">*</span></label>
              <input id="phone" name="phone" type="tel" inputMode="tel" value={formData.phone} onChange={handleChange} onBlur={handleBlur} autoComplete="tel" className={`w-full px-3 py-2 rounded-lg border ${errors.phone ? 'border-danger-500 ring-2 ring-danger-500/20' : 'border-mekomit-200'} focus:outline-none focus:ring-2 focus:ring-mekomit-500 transition-all`} placeholder="050-1234567" />
              {errors.phone && <p className="text-danger-500 text-xs">{errors.phone}</p>}
            </div>
            {(requiresDelivery || isOnPremise) && (
              <>
                {FulfillmentSelector}
                {PickupOrDeliveryFields}
              </>
            )}
            {CouponField}
            <div className="space-y-1">
              <label className="block text-xs font-semibold text-mekomit-900 flex items-center gap-1"><CreditCard className="w-4 h-4" /> אמצעי תשלום <span className="text-danger-500">*</span></label>
              {formData.paymentMethod !== CREDIT_CARD_METHOD && (
                <p className="text-xs text-mekomit-600 bg-info-50 border border-info-100 rounded-lg p-2">
                  <strong>שים לב:</strong> בחירת אמצעי תשלום היא לצורכי רישום בלבד. התשלום עצמו יתבצע בנפרד מתהליך ההזמנה.
                </p>
              )}
              {paymentMethodsLoading ? <div className="text-xs text-mekomit-700 flex items-center gap-2 min-h-[2.5rem]"><Loader2 className="w-4 h-4 animate-spin" /> טוען...</div> : paymentMethodsData?.payment_methods?.length ? (
                <div className="grid grid-cols-2 gap-2 min-h-[2.5rem]">
                  {paymentMethodsData.payment_methods.map(method => (
                    <label
                      key={method}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePaymentMethodClick(method);
                      }}
                      className={`flex items-center justify-center gap-1 px-2 py-2 rounded-lg border-2 cursor-pointer transition-colors text-xs font-semibold ${formData.paymentMethod === method ? 'border-mekomit-500 bg-mekomit-50 shadow-md' : 'border-mekomit-200 hover:border-mekomit-300'}`}
                    >
                      <input type="radio" name="paymentMethod" value={method} checked={formData.paymentMethod === method} readOnly className="sr-only pointer-events-none" />
                      <span className="text-mekomit-900">{method}</span>
                    </label>
                  ))}
                </div>
              ) : <div className="text-danger-500 text-xs min-h-[2.5rem] flex items-center">אין תשלום זמין</div>}
              {errors.paymentMethod && <p className="text-danger-500 text-xs">{errors.paymentMethod}</p>}
              {CreditCardFields}
            </div>
            <div className="space-y-1">
              <label htmlFor="notes" className="block text-xs font-semibold text-mekomit-900 flex items-center gap-1"><FileText className="w-4 h-4" /> הערות (אופציונלי)</label>
              <textarea id="notes" name="notes" rows="3" value={formData.notes} onChange={handleChange} className="w-full px-3 py-2 rounded-lg border border-mekomit-200 focus:outline-none focus:ring-2 focus:ring-mekomit-500 transition-all resize-none text-xs" placeholder="הוסף הערות" />
            </div>
          </div>
        </div>

        {/* Fixed buttons at bottom - iOS safe area aware */}
        <div className="flex-shrink-0 border-t border-mekomit-200 px-5 pt-3 bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))', position: 'sticky', bottom: 0, zIndex: 10 }}>
          <div className="flex flex-col gap-3">
            {SubmitFeedback}
            <button type="submit" disabled={!isFormValid || submitting} className="w-full bg-gradient-to-r from-success-500 to-success-500 hover:from-success-600 hover:to-success-600 disabled:from-line disabled:to-line-strong text-white font-bold py-3 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2">
              {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> {paymentProcessing ? 'מחייב כרטיס...' : (isAppt ? 'קובע תור...' : 'מבצע הזמנה...')}</>) : (<><CheckCircle className="w-5 h-5" /> {formData.paymentMethod === CREDIT_CARD_METHOD ? `שלם ₪${total.toFixed(2)}` : (isAppt ? 'אישור התור' : 'אשר הזמנה')}</>)}
            </button>
            <button type="button" onClick={onCancel} disabled={submitting} className="w-full bg-mekomit-100 hover:bg-mekomit-200 text-mekomit-900 font-bold py-2 rounded-xl transition-all">{isAppt ? 'חזרה' : 'חזור לסל'}</button>
          </div>
        </div>
      </form>
    );
  }

  return (
    <div className="min-h-screen flex justify-center items-start px-4 pt-4 pb-4 fixed inset-0 bg-black/50 backdrop-blur-sm" style={{ paddingBottom: `max(${FOOTER_RESERVED + 16}px, calc(${FOOTER_RESERVED + 16}px + env(safe-area-inset-bottom)))`, zIndex: 10000, height: '100vh', minHeight: '-webkit-fill-available' }}>
      <div className="w-full max-w-xl bg-white rounded-2xl border border-mekomit-100 shadow-xl flex flex-col overflow-hidden" style={{ maxHeight: `calc(100vh - ${FOOTER_RESERVED + 32}px - env(safe-area-inset-bottom))`, height: 'auto' }}>
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 border-b border-mekomit-100 bg-mekomit-50/60 backdrop-blur-sm sticky top-0 z-10">
            <h3 className="text-lg font-bold text-mekomit-900 flex items-center gap-2"><Package className="w-5 h-5" /> {isAppt ? 'סיכום התור' : 'סיכום הזמנה'}</h3>
            {Summary}
            {TimeSlotInfo}
          </div>
          <form onSubmit={handleSubmit} className="p-4 space-y-3">
            <h3 className="text-lg font-bold text-mekomit-900">{isAppt ? 'פרטי המטופל' : 'פרטי הזמנה'}</h3>
            <div className="space-y-1">
              <label htmlFor="name" className="block text-xs font-semibold text-mekomit-900">שם מלא <span className="text-danger-500">*</span></label>
              <input id="name" name="name" value={formData.name} onChange={handleChange} onBlur={handleBlur} autoComplete="name" className={`w-full px-3 py-2 rounded-lg border ${errors.name ? 'border-danger-500 ring-2 ring-danger-500/20' : 'border-mekomit-200'} focus:outline-none focus:ring-2 focus:ring-mekomit-500 transition-all`} placeholder="הזן שם מלא" />
              {errors.name && <p className="text-danger-500 text-xs">{errors.name}</p>}
            </div>
            <div className="space-y-1">
              <label htmlFor="email" className="block text-xs font-semibold text-mekomit-900">אימייל (אופציונלי)</label>
              <input id="email" name="email" type="email" inputMode="email" value={formData.email} onChange={handleChange} autoComplete="email" className="w-full px-3 py-2 rounded-lg border border-mekomit-200 focus:outline-none focus:ring-2 focus:ring-mekomit-500 transition-all" placeholder="example@email.com" />
            </div>
            <div className="space-y-1">
              <label htmlFor="phone" className="block text-xs font-semibold text-mekomit-900">טלפון <span className="text-danger-500">*</span></label>
              <input id="phone" name="phone" type="tel" inputMode="tel" value={formData.phone} onChange={handleChange} onBlur={handleBlur} autoComplete="tel" className={`w-full px-3 py-2 rounded-lg border ${errors.phone ? 'border-danger-500 ring-2 ring-danger-500/20' : 'border-mekomit-200'} focus:outline-none focus:ring-2 focus:ring-mekomit-500 transition-all`} placeholder="050-1234567" />
              {errors.phone && <p className="text-danger-500 text-xs">{errors.phone}</p>}
            </div>
            {(requiresDelivery || isOnPremise) && (
              <>
                {FulfillmentSelector}
                {PickupOrDeliveryFields}
              </>
            )}
            {CouponField}
            <div className="space-y-1">
              <label className="block text-xs font-semibold text-mekomit-900 flex items-center gap-1"><CreditCard className="w-4 h-4" /> אמצעי תשלום <span className="text-danger-500">*</span></label>
              {formData.paymentMethod !== CREDIT_CARD_METHOD && (
                <p className="text-xs text-mekomit-600 bg-info-50 border border-info-100 rounded-lg p-2">
                  <strong>שים לב:</strong> בחירת אמצעי תשלום היא לצורכי רישום בלבד. התשלום עצמו יתבצע בנפרד מתהליך ההזמנה.
                </p>
              )}
              {paymentMethodsLoading ? <div className="text-xs text-mekomit-700 flex items-center gap-2 min-h-[2.5rem]"><Loader2 className="w-4 h-4 animate-spin" /> טוען...</div> : paymentMethodsData?.payment_methods?.length ? (
                <div className="grid grid-cols-2 gap-2 min-h-[2.5rem]">
                  {paymentMethodsData.payment_methods.map(method => (
                    <label
                      key={method}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePaymentMethodClick(method);
                      }}
                      className={`flex items-center justify-center gap-1 px-2 py-2 rounded-lg border-2 cursor-pointer transition-colors text-xs font-semibold ${formData.paymentMethod === method ? 'border-mekomit-500 bg-mekomit-50 shadow-md' : 'border-mekomit-200 hover:border-mekomit-300'}`}
                    >
                      <input type="radio" name="paymentMethod" value={method} checked={formData.paymentMethod === method} readOnly className="sr-only pointer-events-none" />
                      <span className="text-mekomit-900">{method}</span>
                    </label>
                  ))}
                </div>
              ) : <div className="text-danger-500 text-xs min-h-[2.5rem] flex items-center">אין תשלום זמין</div>}
              {errors.paymentMethod && <p className="text-danger-500 text-xs">{errors.paymentMethod}</p>}
              {CreditCardFields}
            </div>
            <div className="space-y-1">
              <label htmlFor="notes" className="block text-xs font-semibold text-mekomit-900 flex items-center gap-1"><FileText className="w-4 h-4" /> הערות (אופציונלי)</label>
              <textarea id="notes" name="notes" rows="3" value={formData.notes} onChange={handleChange} className="w-full px-3 py-2 rounded-lg border border-mekomit-200 focus:outline-none focus:ring-2 focus:ring-mekomit-500 transition-all resize-none text-xs" placeholder="הוסף הערות" />
            </div>
          </form>
        </div>
        <div className="border-t border-mekomit-100 px-4 pt-4 bg-white flex flex-col gap-2" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          {SubmitFeedback}
          <button type="button" disabled={!isFormValid || submitting} onClick={(e) => { if (isFormValid) { handleSubmit(e); } }} className="w-full bg-gradient-to-r from-success-500 to-success-500 hover:from-success-600 hover:to-success-600 disabled:from-line disabled:to-line-strong text-white font-bold py-3 rounded-xl transition-all shadow flex items-center justify-center gap-2 text-sm">
            {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> {paymentProcessing ? 'מחייב כרטיס...' : (isAppt ? 'קובע תור...' : 'מבצע הזמנה...')}</>) : (<><CheckCircle className="w-5 h-5" /> {formData.paymentMethod === CREDIT_CARD_METHOD ? `שלם ₪${total.toFixed(2)}` : (isAppt ? 'אישור התור' : 'אשר הזמנה')}</>)}
          </button>
          <button type="button" onClick={onCancel} disabled={submitting} className="w-full bg-mekomit-100 hover:bg-mekomit-200 text-mekomit-900 font-bold py-2 rounded-xl transition-all text-sm">{isAppt ? 'חזרה' : 'חזור לסל'}</button>
        </div>
      </div>
    </div>
  );
};
